import { supabase } from '../supabase';

export interface ExchangeRate {
  id: number;
  date: string; // YYYY-MM-DD
  base_currency: string;
  currency: string;
  value: number; // e.g., 7.2 means 1 USD = 7.2 RMB
  created_at: string;
}

// Default fallback rate (1 USD = 7.14 RMB, so RMB to USD = 0.14)
export const DEFAULT_EXCHANGE_RATE = 7.14;

/**
 * Get exchange rate for a specific date
 * If not found, returns the closest available rate before that date
 */
export async function getExchangeRate(
  date: string,
  currency: string = 'RMB'
): Promise<{ rate: number; date: string; isDefault: boolean }> {
  try {
    console.log('[getExchangeRate] Fetching for date:', date, 'currency:', currency);

    // First try exact date
    const { data: exactMatch, error: exactError } = await supabase
      .from('exchange_rates')
      .select('value, date')
      .eq('date', date)
      .eq('currency', currency)
      .eq('base_currency', 'USD')
      .limit(1);

    console.log('[getExchangeRate] Exact match result:', { exactMatch, exactError });

    if (!exactError && exactMatch && exactMatch.length > 0) {
      return {
        rate: exactMatch[0].value,
        date: exactMatch[0].date,
        isDefault: false,
      };
    }

    // If not found, get the closest date before
    const { data: closestMatch, error: closestError } = await supabase
      .from('exchange_rates')
      .select('value, date')
      .eq('currency', currency)
      .eq('base_currency', 'USD')
      .lt('date', date)
      .order('date', { ascending: false })
      .limit(1);

    console.log('[getExchangeRate] Closest match result:', { closestMatch, closestError });

    if (!closestError && closestMatch && closestMatch.length > 0) {
      return {
        rate: closestMatch[0].value,
        date: closestMatch[0].date,
        isDefault: false,
      };
    }

    // Fallback to default
    console.log('[getExchangeRate] Using default rate');
    return {
      rate: DEFAULT_EXCHANGE_RATE,
      date: date,
      isDefault: true,
    };
  } catch (err) {
    console.error('[getExchangeRate] Error:', err);
    return {
      rate: DEFAULT_EXCHANGE_RATE,
      date: date,
      isDefault: true,
    };
  }
}

/**
 * Get exchange rates for a date range
 * Returns a map of date -> rate
 */
export async function getExchangeRatesRange(
  from: string,
  to: string,
  currency: string = 'RMB'
): Promise<{
  rates: Map<string, number>;
  average: number;
  min: number;
  max: number;
  count: number;
  isDefault: boolean;
}> {
  try {
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('value, date')
      .eq('currency', currency)
      .eq('base_currency', 'USD')
      .gte('date', from)
      .lte('date', to)
      .order('date', { ascending: true });

    if (error || !data || data.length === 0) {
      // Try to get at least one rate before the range for fallback
      const { data: fallbackData } = await supabase
        .from('exchange_rates')
        .select('value, date')
        .eq('currency', currency)
        .eq('base_currency', 'USD')
        .lte('date', to)
        .order('date', { ascending: false })
        .limit(1);

      if (fallbackData && fallbackData.length > 0) {
        const rates = new Map<string, number>();
        rates.set(fallbackData[0].date, fallbackData[0].value);
        return {
          rates,
          average: fallbackData[0].value,
          min: fallbackData[0].value,
          max: fallbackData[0].value,
          count: 1,
          isDefault: false,
        };
      }

      // Complete fallback to default
      const rates = new Map<string, number>();
      return {
        rates,
        average: DEFAULT_EXCHANGE_RATE,
        min: DEFAULT_EXCHANGE_RATE,
        max: DEFAULT_EXCHANGE_RATE,
        count: 0,
        isDefault: true,
      };
    }

    const rates = new Map<string, number>();
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;

    for (const row of data) {
      rates.set(row.date, row.value);
      sum += row.value;
      min = Math.min(min, row.value);
      max = Math.max(max, row.value);
    }

    return {
      rates,
      average: sum / data.length,
      min,
      max,
      count: data.length,
      isDefault: false,
    };
  } catch {
    const rates = new Map<string, number>();
    return {
      rates,
      average: DEFAULT_EXCHANGE_RATE,
      min: DEFAULT_EXCHANGE_RATE,
      max: DEFAULT_EXCHANGE_RATE,
      count: 0,
      isDefault: true,
    };
  }
}

/**
 * Get the rate to use for a specific date from a rates map
 * Falls back to closest previous date, then default
 */
export function getRateForDate(
  date: string,
  rates: Map<string, number>,
  defaultRate: number = DEFAULT_EXCHANGE_RATE
): number {
  // Exact match
  if (rates.has(date)) {
    return rates.get(date)!;
  }

  // Find closest previous date
  const sortedDates = Array.from(rates.keys()).sort();
  let closestDate: string | null = null;

  for (const d of sortedDates) {
    if (d <= date) {
      closestDate = d;
    } else {
      break;
    }
  }

  if (closestDate) {
    return rates.get(closestDate)!;
  }

  // If no previous date, use the first available or default
  if (sortedDates.length > 0) {
    return rates.get(sortedDates[0])!;
  }

  return defaultRate;
}

/**
 * Get ALL exchange rates from the database
 * Useful for comparing across multiple seasons/years
 * Returns a map of date -> rate for quick lookups
 */
export async function getAllExchangeRates(
  currency: string = 'RMB'
): Promise<{
  rates: Map<string, number>;
  count: number;
  isDefault: boolean;
}> {
  try {
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('value, date')
      .eq('currency', currency)
      .eq('base_currency', 'USD')
      .order('date', { ascending: true });

    if (error || !data || data.length === 0) {
      console.warn('[getAllExchangeRates] No data found, using default');
      return {
        rates: new Map(),
        count: 0,
        isDefault: true,
      };
    }

    const rates = new Map<string, number>();
    for (const row of data) {
      rates.set(row.date, row.value);
    }

    console.log(`[getAllExchangeRates] Loaded ${data.length} exchange rates`);

    return {
      rates,
      count: data.length,
      isDefault: false,
    };
  } catch (err) {
    console.error('[getAllExchangeRates] Error:', err);
    return {
      rates: new Map(),
      count: 0,
      isDefault: true,
    };
  }
}

/**
 * Convert RMB to USD using a specific rate
 * rate is USD to RMB (e.g., 7.2), so we divide
 */
export function convertRmbToUsd(rmb: number, rate: number): number {
  return rmb / rate;
}

/**
 * Convert USD to RMB using a specific rate
 */
export function convertUsdToRmb(usd: number, rate: number): number {
  return usd * rate;
}
